<template>
  <div class="gg-ghcols">
    <div v-if="error" class="gg-ghcols__error">{{ error }}</div>
    <div class="gg-ghcols__scroll">
      <div v-for="(col, i) in columns" :key="i" class="gg-ghcol">
        <div class="gg-ghcol__head" :title="col.title">
          <v-icon size="16" class="gg-ghcol__head-icon">
            {{ i === 0 ? 'mdi-graph-outline' : 'mdi-folder-open' }}
          </v-icon>
          {{ col.title }}
        </div>
        <div class="gg-ghcol__body">
          <div v-if="col.loading" class="gg-ghcol__loading">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <button
              v-for="folder in col.folders"
              :key="folder.uuid"
              type="button"
              class="gg-ghrow gg-ghrow--folder"
              :class="{ 'is-active': col.selectedUuid === folder.uuid }"
              @click="openFolder(i, folder)"
            >
              <v-icon size="18" class="gg-ghrow__icon">mdi-folder</v-icon>
              <span class="gg-ghrow__name">{{ folder.title }}</span>
              <v-icon size="18" class="gg-ghrow__chev">mdi-chevron-right</v-icon>
            </button>
            <div v-for="file in col.files" :key="file.uuid" class="gg-ghrow gg-ghrow--file">
              <v-icon size="18" class="gg-ghrow__icon">mdi-file-image-outline</v-icon>
              <span class="gg-ghrow__name">{{ file.title }}</span>
            </div>
            <div v-if="!col.folders.length && !col.files.length" class="gg-ghcol__empty">
              Pasta vazia
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Graphic Hub browser as Miller columns: each folder opens its contents in a new
 * column to the RIGHT (folders keep cascading rightward down to the leaf items).
 * Self-contained — fetches each column's contents directly by uuid so it doesn't
 * clash with the shared single-location browse state (globographics/getterFolders).
 * Endpoints: GET globographics/folders (root) · /folders/:uuid (subfolders) ·
 * /folders/:uuid/files (items).
 */
import { apiClient } from '@/services/api';

export default {
  name: 'GloboGraphicsGhColumns',
  data() {
    return { columns: [], error: null, token: 0 };
  },
  created() {
    this.loadRoot();
  },
  methods: {
    onlyFolders(data) {
      return (data || []).filter((f) => !f.type || f.type === 'Folder' || f.type === 'Project');
    },
    async loadRoot() {
      this.error = null;
      const col = { title: 'Graphic Hub', uuid: null, folders: [], files: [], loading: true, selectedUuid: null };
      this.columns = [col];
      try {
        const { data } = await apiClient.get('globographics/folders');
        col.folders = this.onlyFolders(data);
      } catch (e) {
        this.error = e.response?.data?.error || 'Erro ao carregar pastas do Graphic Hub';
      } finally {
        col.loading = false;
      }
    },
    async openFolder(colIndex, folder) {
      // Mark the clicked folder in its column; drop any deeper columns; open a new
      // column for its contents.
      this.columns[colIndex].selectedUuid = folder.uuid;
      this.columns = this.columns.slice(0, colIndex + 1);
      const newCol = { title: folder.title, uuid: folder.uuid, folders: [], files: [], loading: true, selectedUuid: null };
      this.columns.push(newCol);
      const myToken = ++this.token;
      try {
        const [foldersRes, filesRes] = await Promise.all([
          apiClient.get(`globographics/folders/${folder.uuid}`),
          apiClient.get(`globographics/folders/${folder.uuid}/files`).catch(() => ({ data: [] })),
        ]);
        // A newer click superseded this one — drop the stale result.
        if (myToken !== this.token) return;
        newCol.folders = this.onlyFolders(foldersRes.data);
        newCol.files = filesRes.data || [];
      } catch (e) {
        if (myToken === this.token) this.error = e.response?.data?.error || 'Erro ao abrir a pasta';
      } finally {
        newCol.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-ghcols {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.gg-ghcols__error {
  padding: var(--sc-space-2) var(--sc-space-3);
  margin-bottom: var(--sc-space-2);
  border-radius: var(--sc-radius-md);
  background: rgba(var(--sc-color-error-rgb), 0.12);
  color: var(--sc-gg-critical);
  font-size: 13px;
}

.gg-ghcols__scroll {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
}

.gg-ghcol {
  display: flex;
  flex-direction: column;
  flex: 0 0 280px;
  width: 280px;
  min-height: 0;
  border-right: 1px solid var(--sc-gg-border-100);

  &:last-child {
    border-right: none;
  }
}

.gg-ghcol__head {
  display: flex;
  align-items: center;
  gap: var(--sc-space-2);
  padding: var(--sc-space-2) var(--sc-space-3);
  flex-shrink: 0;
  border-bottom: 1px solid var(--sc-gg-border-100);
  background: var(--sc-gg-surface-2);
  color: var(--sc-gg-text-secondary);
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .gg-ghcol__head-icon {
    color: var(--sc-gg-text-tertiary) !important;
  }
}

.gg-ghcol__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--sc-space-1);
}

.gg-ghcol__loading,
.gg-ghcol__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sc-space-6);
  color: var(--sc-gg-text-tertiary);
  font-size: 13px;
}

.gg-ghrow {
  display: flex;
  align-items: center;
  gap: var(--sc-space-2);
  width: 100%;
  padding: var(--sc-space-2);
  border-radius: var(--sc-radius-sm);
  color: var(--sc-gg-text-secondary);
  text-align: left;

  .gg-ghrow__icon {
    color: var(--sc-gg-text-tertiary) !important;
    flex-shrink: 0;
  }

  .gg-ghrow__chev {
    color: var(--sc-gg-text-tertiary) !important;
    flex-shrink: 0;
  }
}

.gg-ghrow__name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gg-ghrow--folder {
  cursor: pointer;

  &:hover {
    background: var(--sc-gg-surface-2);
  }

  &.is-active {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
    color: var(--sc-gg-text-primary);

    .gg-ghrow__icon,
    .gg-ghrow__chev {
      color: var(--sc-gg-accent) !important;
    }
  }
}

.gg-ghrow--file {
  cursor: default;
}
</style>
